import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { TextField, Typography } from "@mui/material";
import axios from "../../api/axios";
import { useToken } from "../../Hooks/useToken";
import styles from "./ForgotPassword.module.css";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [token] = useToken();

  // Redirect if user is already logged in
  useEffect(() => {
    if (token) {
      navigate('/events', { replace: true });
    }
  }, [token, navigate]);

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Real-time validation functions
  const validateField = (fieldName, value) => {
    const newErrors = { ...errors };
    
    switch (fieldName) {
      case 'email':
        if (!value || value.trim() === '') {
          newErrors.email = 'Email is required';
        } else if (!validateEmail(value)) {
          newErrors.email = 'Please enter a valid email address';
        } else {
          delete newErrors.email;
        }
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if form is valid and clear errors when fields become valid
  useEffect(() => {
    const hasEmailError = !email || email.trim() === '' || !validateEmail(email);
    
    const isValid = !hasEmailError;
    
    setIsFormValid(isValid);
    
    // Clear errors for valid fields
    const newErrors = { ...errors };
    if (!hasEmailError && errors.email) {
      delete newErrors.email;
    }
    setErrors(newErrors);
  }, [email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isFormValid) {
      enqueueSnackbar("Please enter a valid email address.", { variant: 'error' });
      return;
    }

    setIsSubmitting(true);
    setShowSuccessMessage(false);
    
    try {
      // Send password reset request
      await sendPasswordResetRequest(email);
      
      // Show success message
      setShowSuccessMessage(true);
      enqueueSnackbar("Password reset instructions have been sent to your email.", { 
        variant: 'success',
        autoHideDuration: 8000
      });
      
      // Clear form
      setEmail("");
      setErrors({});
      setIsFormValid(false);
      
      // Redirect after a delay
      setTimeout(() => {
        navigate("/login");
      }, 5000);
      
    } catch (error) {
      // Handle specific error cases
      if (error.response?.status === 404) {
        enqueueSnackbar("No account found with this email address. Please check your email or create a new account.", { 
          variant: 'error',
          autoHideDuration: 8000
        });
      } else if (error.response?.status === 429) {
        enqueueSnackbar("Too many reset requests. Please wait a few minutes before trying again.", { 
          variant: 'error',
          autoHideDuration: 6000
        });
      } else if (error.response?.status >= 500) {
        enqueueSnackbar("Server error occurred. Please try again later.", { 
          variant: 'error',
          autoHideDuration: 6000
        });
      } else if (!navigator.onLine) {
        enqueueSnackbar("No internet connection. Please check your connection and try again.", { 
          variant: 'error',
          autoHideDuration: 6000
        });
      } else {
        enqueueSnackbar("Failed to send reset instructions. Please try again or contact support.", { 
          variant: 'error',
          autoHideDuration: 6000
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Send password reset request to API
  const sendPasswordResetRequest = async (email) => {
    const data = {
      email: email,
      resetType: 'password'
    };
    
    const response = await axios.post("/api/auth/forgot-password", JSON.stringify(data));
    return response.data;
  };

  return (
    <>
      <div className={styles.accountNav}>
        <Typography className={styles.navText}>
          Sharing God's Love in Word and Deed
        </Typography>
      </div>
      <div className={styles.forgotPasswordMain}>
        <div id="app" className={styles.contentHalf}>
          <section className={styles.forgotPasswordFormSection}>
            <img
              src="./Images/baptists-on-mission-logo.png"
              alt="Baptists on Mission Logo"
              className={styles.bomLogo}
            />
            <div className={styles.formHeader}>
              <Typography variant="h4" className={styles.title}>
                Forgot Password?
              </Typography>
              <Typography variant="body1" className={styles.subtitle}>
                No worries! Enter your email address and we'll send you instructions to reset your password.
              </Typography>
            </div>
            
            <form onSubmit={handleSubmit} className={styles.forgotPasswordForm}>
              <div className={styles.formGroup}>
                <Typography variant="formLabel" className={styles.requiredLabel}>
                  Email Address
                </Typography>
                <TextField
                  type="email"
                  id="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    validateField('email', e.target.value);
                  }}
                  error={!!errors.email}
                  fullWidth
                />
                {errors.email && (
                  <Typography variant="caption" color="error" className={styles.errorText}>
                    {errors.email}
                  </Typography>
                )}
              </div>
              
              <button
                type="submit"
                className="button-default button-full-width"
                disabled={!isFormValid || isSubmitting}
              >
                {isSubmitting ? 'Sending Instructions...' : 'Send Reset Instructions'}
              </button>
              
              {showSuccessMessage && (
                <div className={styles.successMessage}>
                  <Typography variant="h6" className={styles.successTitle}>
                    📧 Instructions Sent!
                  </Typography>
                  <Typography variant="body2" className={styles.successText}>
                    We've sent password reset instructions to your email address. Please check your inbox and follow the instructions to reset your password.
                  </Typography>
                  <Typography variant="caption" className={styles.redirectText}>
                    Redirecting to login page in 5 seconds...
                  </Typography>
                </div>
              )}
            </form>
            
            <div className={styles.authLinks}>
              <p className="mb-3">
                <Link to="/login">← Back to Sign In</Link>
              </p>
              <p className="mb-5">
                Don't have an account?{" "}
                <Link to="/createaccount">Create Account</Link>
              </p>
            </div>
            
            <p className="problem-text">
              If you have any problems, please contact us at (800) 395-5102 ext 5596
            </p>
          </section>
        </div>
        <img
          className={styles.forgotPasswordImg}
          src="./Images/sign-in-page-image.jpg"
          alt="Baptists on Mission forgot password"
        />
      </div>
    </>
  );
};

export default ForgotPassword;
